import React, { useState, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { trackCreateProjectError } from '../../../analytics';

import { Button } from '../../../components/general';
import {
  PageWrapper,
  PageContent,
  ContentHeader,
  BackBtn,
} from '../../../components/layout/content';
import { ErrorModal } from '../../../components/modals';

import { URL, EVENT_TYPE_VIDEOGRAPHER } from '../../../constants';

import useAuthUserStore from '../../../hooks/useAuthUserStore';

import { createEvent, getUserEvents } from '../../../services/api/events';

import { useProjectStore } from '../../../stores/project';
import { useProjectSettingsStore } from '../../../stores/projectSettings';

import { Event, PageError } from '../../../types';
import { isEmpty, getUrl, canEditEvent, isProduction } from '../../../utils';

import CheckInTab from './CheckInTab/checkInTab';
import EmailHubTab from './EmailHubTab/emailHub';
import EmailSelection from './EmailHubTab/emailSelection';
import GeneralTab from './GeneralTab/generalTab';
import ShareVideoTab from './ShareVideoTab/shareVideoTab';

export const SETTINGS_PREVIEW_EVENT_ID = isProduction()
  ? 'eb6e8339-32f7-4787-924d-c74040644410'
  : '42569265-cbdd-4f23-bd7c-9fccc14ac542';

export const SETTINGS_PREVIEW_FACE_ID = isProduction()
  ? 'd2da39b4-1f3f-40a1-bf05-497e98b0802b'
  : '62b810c1-5c08-46dd-9173-f5afe157d73b';

const ProjectSettings = () => {
  const { eventId = '', tab, subtab } = useParams();

  const queryParameters = new URLSearchParams(window.location.search);
  const isAttendeeReview = queryParameters.get('isAttendeeReview') === 'true';

  const { user } = useAuthUserStore();
  const {
    eventId: eventIdLocal,
    currentProject,
    projectError,
    setEventId,
  } = useProjectStore();

  const {
    previewPerson,
    setPreviewPerson,
    allEmailTemplates,
    setAllEmailTemplates,
    customEmailTemplates,
    setCustomEmailTemplates,
    footer,
    setFooter,
  } = useProjectSettingsStore();

  const currentEventRef = useRef(currentProject);

  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState<PageError>(null);

  const navigate = useNavigate();

  const isMobileDevice = useMemo(
    () => window.innerWidth < 1024,
    [window.innerWidth],
  );

  // set tab if not given
  useEffect(() => {
    if (eventId && !tab && !isMobileDevice)
      navigate(getUrl(URL.PROJECT_SETTINGS, eventId, 'general'));
  }, [eventId, navigate, tab, isMobileDevice]);

  // set event id on mount
  useEffect(() => {
    if (!!eventId && eventIdLocal !== eventId) setEventId(eventId);
  }, [eventId, eventIdLocal, setEventId]);

  // fetch data on mount
  useEffect(() => {
    const onMount = async () => {
      setIsLoading(true);

      if (!eventId) await handleCreateEvent();
      else if (projectError) {
        setPageError(projectError);
        setIsLoading(false);
      } else if (currentProject) {
        const isEventChanged = currentEventRef.current !== currentProject;

        switch (tab) {
          case 'share-video':
            if (isEmpty(previewPerson)) await setPreviewPerson();
            break;

          case 'email-hub':
            if (isEmpty(allEmailTemplates) || isEventChanged)
              await setAllEmailTemplates(currentProject);

            if (isEmpty(footer)) await setFooter();

            if (
              subtab &&
              (isEmpty(customEmailTemplates[subtab]) || isEventChanged)
            )
              await setCustomEmailTemplates(subtab);
            break;

          default:
            break;
        }

        currentEventRef.current = currentProject;

        setIsLoading(false);
      }
    };

    onMount();
  }, [currentProject, projectError, subtab, tab]);

  const handleCreateEvent = async () => {
    const newEvent: Partial<Event> = {
      userId: user.session.userId,
      name: 'Untitled',
      type: EVENT_TYPE_VIDEOGRAPHER,
    };

    if (isAttendeeReview)
      newEvent.attendeeReviewData = { isAttendeeReview: true };

    try {
      const event = await createEvent(newEvent);
      const eventId = event.eventId;

      await getUserEvents(user.session.userId);

      navigate(getUrl(URL.VIDEO_BUILDER, eventId));
    } catch {
      trackCreateProjectError();
      toast.error('Something went wrong. Try again later');
      navigate(URL.HOME);
    }
  };

  const pathname = window.location.pathname;

  const pageTabs = [
    { label: 'General', value: 'general' },
    { label: 'Check-In Page', value: 'check-in' },
    { label: 'Share Video Page', value: 'share-video' },
    { label: 'Email Hub', value: 'email-hub' },
  ];

  const renderContent = () => {
    if (!currentProject) return null;

    switch (tab) {
      case 'check-in':
        return <CheckInTab event={currentProject} />;

      case 'share-video':
        return <ShareVideoTab event={currentProject} />;

      case 'email-hub':
        if (subtab)
          return <EmailSelection event={currentProject} emailType={subtab} />;
        return <EmailHubTab event={currentProject} />;

      case 'general':
      default:
        return <GeneralTab event={currentProject} />;
    }
  };

  const backBtnData = useMemo(() => {
    if (tab === 'email-hub' && !!subtab)
      return {
        label: 'Email Hub',
        url: getUrl(URL.PROJECT_SETTINGS, eventId, 'email-hub'),
      };

    return {
      label: 'Project Settings',
      url: getUrl(URL.PROJECT_SETTINGS, eventId),
      className: classNames({ 'flex lg:hidden': !!tab, hidden: !tab }),
    };
  }, [eventId, subtab, tab]);

  return (
    <PageWrapper
      type="project"
      title={`Project Settings – ${currentProject?.name}`}
      pageError={pageError}
      className="gap-6 sm:gap-8"
    >
      <ContentHeader title="Project Settings" />

      <div className="flex flex-col lg:flex-row gap-8">
        {/* desktop menu */}
        <div className="hidden lg:block w-1/6">
          <div className="w-full sticky top-0">
            {pageTabs.map((pageTab) => (
              <Button
                key={pageTab.value}
                onClick={() =>
                  navigate(getUrl(URL.PROJECT_SETTINGS, eventId, pageTab.value))
                }
                variant="primary-thin"
                colour="transparent-grey"
                className={classNames(
                  'w-full text-sm text-left',
                  pathname.includes(pageTab.value)
                    ? 'font-semibold'
                    : 'text-black/[0.5]',
                )}
              >
                <div className="w-full">{pageTab.label}</div>
              </Button>
            ))}
          </div>
        </div>

        {/* mobile menu */}
        <div
          className={classNames({ 'w-full lg:hidden': !tab, hidden: !!tab })}
        >
          {pageTabs.map((pageTab) => (
            <Button
              key={pageTab.value}
              onClick={() =>
                navigate(getUrl(URL.PROJECT_SETTINGS, eventId, pageTab.value))
              }
              colour="custom"
              className="w-full px-[5vw] text-sm text-left first:border-t border-b border-grey-200 border-solid rounded-none"
            >
              <div className="w-full">{pageTab.label}</div>
            </Button>
          ))}
        </div>

        <div className="w-full lg:w-5/6">
          {/* back btn */}
          <BackBtn
            onClick={() => navigate(backBtnData?.url)}
            label={backBtnData?.label}
            className={backBtnData?.className}
          />

          {/* content */}
          <div
            className={classNames({
              'hidden lg:block': !tab,
              'lg:h-screen lg:-mt-[calc((var(--page-spacing)_*_2)_+_2rem_+_(2.5rem_*_1.25))]':
                isLoading,
            })}
          >
            <PageContent isLoading={isLoading}>{renderContent()}</PageContent>
          </div>
        </div>
      </div>

      <ErrorModal
        show={!isLoading && !canEditEvent(currentProject)}
        heading="Sorry, this project cannot be edited"
        subheading="You may not have access to edit this project or it has already started."
      />
    </PageWrapper>
  );
};

export default ProjectSettings;
